import ActionsPopover from "@/components/ActionsPopOver/ActionsPopOver";
import ActionsPopOverItem from "@/components/ActionsPopOver/ActionsPopOverItem";
import HelpButton from "@/components/HelpButton/HelpButton";
import ImpactPill from "@/components/Impact/ImpactPill";
import { PageHeader } from "@/components/PageHeader/PageHeader";
import StatusTag from "@/components/StatusTag/StatusTag";
import StatusTags from "@/components/StatusTags/StatusTags";
import SwitchWithLabel from "@/components/SwitchWithLabel/SwitchWithLabel";
import Table, { Column } from "@/components/Table/Table";
import useTableStateUrl from "@/components/Table/hooks/useTableStateUrl";
import TableHeader from "@/components/TableHeader/TableHeader";
import YearSelector from "@/components/YearSelector/YearSelector";
import useTrackOnLoad from "@/hooks/useTrackPageview";
import useUrlStateBoolean from "@/hooks/useUrlStateBoolean";
import { useDashboardYear } from "@/pages/dashboard/hooks/useDateRange";
import { DeleteOutlined, MoreOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Tooltip } from "antd";
import { useState } from "react";
import DeleteGoodsAndServicesItemAction from "../DeleteGoodsAndServicesItemAction";
import {
  GoodsAndServicesItem,
  useGetGoodsAndServicesItems,
  useGoodsAndServicesItemStatusPerYear,
} from "../useGoodsAndServices";
import AddGoodsAndServicesItem from "./AddGoodsAndServicesItem";
import styles from "./GoodsAndServicesOverview.module.scss";

export default function GoodsAndServicesOverview() {
  useTrackOnLoad("Goods & Services Viewed");

  const { year } = useDashboardYear();
  const [showUnrelated, setShowUnrelated] = useUrlStateBoolean("showUnrelated", false);

  const columns: Column<GoodsAndServicesItem>[] = [
    {
      key: "name",
      label: "Name",
      maxWidth: "1fr",
      minWidth: "160px",
      sortable: true,
      searchable: true,
    },
    {
      key: "dataset_name",
      label: "Match",
      maxWidth: "3fr",
      minWidth: "160px",
      sortable: true,
      searchable: true,
      renderer: (record) => record.impact_dataset?.name || "-",
    },
    {
      key: "impact",
      label: "Impact",
      maxWidth: "250px",
      minWidth: "250px",
      sortable: true,
      searchable: true,
      renderer: (record) => <ImpactPill impact={record.impact_dataset?.impact} />,
    },
    {
      key: "facility_relations",
      label: "Facility relations",
      maxWidth: "180px",
      minWidth: "146px",
      sortable: true,
    },
    {
      key: "status",
      label: "Status",
      minWidth: "150px",
      sortable: true,
      renderer: (product) => (
        <StatusTag
          status={product.status}
          showIcon={false}
        />
      ),
      filterable: true,
    },
    {
      key: "actions",
      label: "",
      minWidth: "50px",
      maxWidth: "50px",
      noLink: true,
      renderer: (goodsAndServicesItem) => (
        <ActionsPopover
          actions={
            <>
              <DeleteGoodsAndServicesItemAction goodsAndServicesItem={goodsAndServicesItem}>
                <>
                  <ActionsPopOverItem
                    icon={<DeleteOutlined />}
                    disabled={!goodsAndServicesItem.deletable}
                    danger
                  >
                    Delete
                  </ActionsPopOverItem>
                </>
              </DeleteGoodsAndServicesItemAction>
            </>
          }
        >
          <MoreOutlined />
        </ActionsPopover>
      ),
      align: "end",
    },
  ];

  const [isAddModalOpen, setIsAddModelOpen] = useState(false);

  const { pagination, filter, search, sort, onSearchFilterSortPaginate, setPage } =
    useTableStateUrl({
      columns,
      defaultSort: { key: "external_id", order: "ascend" },
    });

  const { data, isLoading, isFetching } = useGetGoodsAndServicesItems({
    filter,
    sort,
    search,
    pagination,
    year,
    showUnrelated,
  });

  const { data: yearsData, isLoading: yearsIsLoading } = useGoodsAndServicesItemStatusPerYear();

  const resetTable = () => {
    setPage(1);
  };

  return (
    <div className={styles.ProductsOverview}>
      <PageHeader
        title="Goods & services"
        description="Your goods & services database built from items used in facilities. Create, manage, and assign impact references to goods and services here."
        right={
          <>
            <HelpButton url="#" />
          </>
        }
      />
      <TableHeader
        left={
          <>
            <YearSelector
              optionData={yearsData}
              isLoading={yearsIsLoading}
              disableYears={showUnrelated}
              onChange={resetTable}
              right={
                <Tooltip
                  placement="bottom"
                  title="Show goods and services that are not related to a year through facilities"
                >
                  <>
                    <SwitchWithLabel
                      togglePosition="right"
                      size="small"
                      checked={showUnrelated}
                      onChange={(disabled) => {
                        setShowUnrelated(disabled);
                      }}
                    >
                      Year unrelated
                    </SwitchWithLabel>
                  </>
                </Tooltip>
              }
            />
            <Button
              icon={<PlusOutlined />}
              onClick={() => setIsAddModelOpen(true)}
            >
              Add
            </Button>
          </>
        }
        right={
          <StatusTags
            data={data}
            loading={isLoading}
          />
        }
      />
      <Table<GoodsAndServicesItem>
        availableFilters={{
          status: [
            { label: "Complete", value: "complete" },
            { label: "Incomplete", value: "incomplete" },
          ],
        }}
        loading={isLoading || isFetching}
        rowKey="id"
        data={data?.data}
        onSearchFilterSortPaginate={onSearchFilterSortPaginate}
        totalRows={data?.total_count}
        pagination={pagination}
        sort={sort}
        search={search}
        filter={filter}
        columns={columns}
        rowLink={(record) => `/goods_and_services/${record.id}`}
      />
      <AddGoodsAndServicesItem
        width={400}
        open={isAddModalOpen}
        afterClose={() => setIsAddModelOpen(false)}
        onCancel={() => setIsAddModelOpen(false)}
      />
    </div>
  );
}
